import { useCallback, useState } from 'react';

import {
  type ErrorLike,
  type InternalRefetchQueriesInclude,
} from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type {
  InsertListingLotsInsertInput,
  InsertListingPropertiesInsertInput,
  Lots,
} from '@graphql/__generated__/graphql';
import {
  type FormDefinitionType,
  RaForm,
  createCheckboxElement,
  createCheckboxGroupElement,
} from '@modules/_shared/components/form/RaForm';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  type NonObjectFields,
  getAddAllSchemaFieldsLink,
} from '@modules/_shared/utils/schema-introspection';
import { CREATE_LISTING } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

interface ListingDuplicateDialogProps {
  listingId: string;
  open: boolean;
  onClose: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
  onSuccess: (lotId: string) => void;
}

const GET_LISTING_DUPLICATE_DIALOG_DATA = gql(/* GraphQL */ `
  query GetListingDuplicateDialogData($id: uuid!) {
    lots_by_pk(id: $id) {
      property {
        id
        property_images_aggregate {
          aggregate {
            count
          }
        }
        property_files_aggregate {
          aggregate {
            count
          }
        }
      }
    }
  }
`);

const GET_LISTING_DUPLICATION_DATA = gql(/* GraphQL */ `
  query GetListingDuplicationData(
    $id: uuid!
    $includeDocuments: Boolean!
    $includePhotos: Boolean!
  ) {
    lots_by_pk(id: $id) {
      id
      use_custom_brochure_description
      brochure_description_en
      brochure_description_fr
      brochure_description_de
      brochure_description_it
      brochure_description_es
      brochure_description_pl
      brochure_description_pt
      property {
        id
        property_files @include(if: $includeDocuments) {
          file_id
          property_id
          file {
            id
          }
        }
        property_images @include(if: $includePhotos) {
          id
          property_id
          image_id
          image {
            id
          }
        }
      }
    }
  }
`);

type TOptions = {
  listing: boolean;
  photos: boolean;
  documents: boolean;
  status: boolean;
};

const DuplicateDialogFooterButtons: React.FC<{ onCancel: () => void }> = ({
  onCancel,
}) => {
  const { t } = useLocale();
  const {
    formState: { isSubmitting },
  } = useFormContext();
  const values = useWatch<TOptions>();

  const canDuplicate = Object.values(values).some(v => v);

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
      }}
    >
      <Box
        component={Paper}
        elevation={3}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          padding: 1,
          borderRadius: 0,
        }}
      >
        <Button
          variant="outlined"
          color="neutral"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {t('Cancel')}
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          disabled={!canDuplicate}
          loading={isSubmitting}
          loadingIndicator={
            <CircularProgress disableShrink color="inherit" size={16} />
          }
        >
          {t('Duplicate')}
        </LoadingButton>
      </Box>
    </div>
  );
};

export const ListingDuplicateDialog: React.FC<ListingDuplicateDialogProps> = ({
  listingId,
  open,
  onClose,
  refetchQueries,
  onSuccess,
}) => {
  const { t } = useLocale();
  const { breakpoints } = useTheme();
  const fullScreen = useMediaQuery(breakpoints.down('sm'));

  return (
    <Dialog maxWidth="sm" fullScreen={fullScreen} open={open} onClose={onClose}>
      <DialogTitle>
        {t('Which data do you want to copy over to the new listing?')}
      </DialogTitle>

      <ListingDuplicateDialogContent
        listingId={listingId}
        onClose={onClose}
        refetchQueries={refetchQueries}
        onSuccess={onSuccess}
      />
    </Dialog>
  );
};

const addAllAvailableFieldsLink = getAddAllSchemaFieldsLink([
  'query_root',
  'lots',
  'properties',
  'property_files',
  'property_images',
  'images',
  'files',
]);

const ListingDuplicateDialogContent: React.FC<
  Omit<ListingDuplicateDialogProps, 'open'>
> = ({ listingId, onClose, refetchQueries, onSuccess }) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { data, error, loading } = useQuery(GET_LISTING_DUPLICATE_DIALOG_DATA, {
    variables: { id: listingId },
    fetchPolicy: 'no-cache',
  });
  const [mutationError, setMutationError] = useState<ErrorLike | null>(null);
  const [getListingDuplicationData] = useLazyQuery(
    GET_LISTING_DUPLICATION_DATA,
    { fetchPolicy: 'no-cache' },
  );
  const [createListing] = useMutation(CREATE_LISTING, {
    onError: setMutationError,
    refetchQueries,
  });

  const photosCount =
    data?.lots_by_pk?.property.property_images_aggregate.aggregate?.count;
  const documentsCount =
    data?.lots_by_pk?.property.property_files_aggregate.aggregate?.count;

  const formDefinitionType: FormDefinitionType<TOptions> = useCallback(
    () => [
      createCheckboxGroupElement(
        'options',
        '',
        [
          createCheckboxElement(
            'listing',
            t('Listing information (Title, description, price, etc...)'),
            { disabled: (_: TOptions) => true },
          ),
          createCheckboxElement('status', t('Status')),
          createCheckboxElement(
            'photos',
            photosCount == null
              ? t('photos')
              : t('{{count}} photos', { count: photosCount }),
          ),
          createCheckboxElement(
            'documents',
            documentsCount == null
              ? t('documents')
              : t('{{count}} documents', { count: documentsCount }),
          ),
        ],
        { gridProps: { md: 12 } },
      ),
    ],
    [t, photosCount, documentsCount],
  );

  const onSubmitted = useCallback(
    async ({ photos, documents, status }: TOptions) => {
      const listingDuplicationData = await getListingDuplicationData({
        variables: {
          id: listingId,
          includeDocuments: documents,
          includePhotos: photos,
        },
        context: {
          customLink: addAllAvailableFieldsLink,
        },
      });

      if (listingDuplicationData.data?.lots_by_pk == null) {
        setMutationError(new Error('Listing not found'));
        return;
      }

      const {
        property: { property_files, property_images, ...propertyData },
        ...listingData
      } = listingDuplicationData.data.lots_by_pk as unknown as NonObjectFields<
        Lots,
        | 'property'
        | 'property.property_files'
        | 'property.property_files.file'
        | 'property.property_images'
        | 'property.property_images.image'
      >;

      delete listingData.created_at;
      delete listingData.created_by;
      delete listingData.updated_at;
      delete listingData.crm_link;
      delete listingData.lead_id;
      // Will be generated by the hasura action.
      delete listingData.lot_reference;

      const forbiddenKeys = [
        'id',
        'centroid',
        'created_at',
        'created_by',
        'updated_at',
        'computed_surface',
        'session_id',
        'latest_appraisal_id',
        'tenant_id',
        'deleted_by_user',
      ];
      const propertyInput: InsertListingPropertiesInsertInput = Object.entries(
        propertyData,
      ).reduce((acc, [key, value]) => {
        if (!forbiddenKeys.includes(key)) {
          acc[key as keyof InsertListingPropertiesInsertInput] = value;
        }

        return acc;
      }, {} as InsertListingPropertiesInsertInput);

      // Because enums from InsertListingLotsInsertInput differs from LotsInsertInput (type generation of action).
      const duplicatedData: InsertListingLotsInsertInput = {
        ...(listingData as unknown as InsertListingLotsInsertInput),
        broker_id: me?.id,
        use_custom_brochure_description:
          listingData.use_custom_brochure_description,
        brochure_description_en: listingData.brochure_description_en,
        brochure_description_fr: listingData.brochure_description_fr,
        brochure_description_de: listingData.brochure_description_de,
        brochure_description_it: listingData.brochure_description_it,
        brochure_description_es: listingData.brochure_description_es,
        brochure_description_pl: listingData.brochure_description_pl,
        brochure_description_pt: listingData.brochure_description_pt,
        title:
          listingData.title != null
            ? `Copy: ${listingData.title}`
            : listingData.title,
      };

      duplicatedData.property = {
        data: propertyInput,
      };

      delete duplicatedData.id;
      delete duplicatedData.property_id;
      delete duplicatedData.order_nr;
      delete duplicatedData.tenant_id;
      delete duplicatedData.monthly_rent_computed;

      if (property_files != null && property_files.length > 0) {
        duplicatedData.property.data.property_files = {
          data: property_files.map(({ file, ...propertyFile }) => ({
            ...propertyFile,
            id: undefined,
            file_id: undefined,
            property_id: undefined,
            tenant_id: undefined,
            file: {
              data: {
                ...file,
                id: undefined,
                created_at: undefined,
                updated_at: undefined,
                tenant_id: undefined,
                uploader_id: undefined,
              },
            },
          })),
        };
      }

      if (property_images != null && property_images.length > 0) {
        duplicatedData.property.data.property_images = {
          data: property_images
            .sort((a, b) => a.order_nr - b.order_nr)
            .map(({ image: { url }, ...propertyImage }) => ({
              ...propertyImage,
              id: undefined,
              created_at: undefined,
              updated_at: undefined,
              property_id: undefined,
              image_id: undefined,
              watermarked_url: undefined,
              image: {
                data: {
                  url,
                },
              },
            })),
        };
      }

      if (!status) {
        delete duplicatedData.status;
      }

      const result = await createListing({
        variables: { lot: duplicatedData },
      });

      if (result.data?.insert_lots_derived != null) {
        onSuccess(result.data.insert_lots_derived.id);
      }
    },
    [listingId, getListingDuplicationData, createListing, onSuccess, me?.id],
  );

  if (error != null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <RaForm
        defaultValues={{
          listing: true,
          status: false,
          photos: false,
          documents: false,
        }}
        formDefinition={formDefinitionType}
        onSubmit={onSubmitted}
        onCancel={onClose}
        actionButtonsComponent={
          <DuplicateDialogFooterButtons onCancel={onClose} />
        }
      />
      <MutationErrorSnackbar
        error={mutationError}
        onClose={() => setMutationError(null)}
      />
    </>
  );
};
